import { useAuth } from "@/src/contexts/auth.context";
import {
  DeleteEventGalleryResponse,
  DeleteEventResponse,
  DeleteRSVPResponse,
  GetEventGalleryResponse,
  GetEventsInput,
  GetEventsResponse,
  GetRSVPsResponse,
  InsertEventGalleryResponse,
  InsertEventInput,
  InsertEventResponse,
  InsertRSVPResponse,
  RSVPParticipantSnapshot,
  RSVPStatus,
  UpdateRSVPResponse,
} from "@/src/models/event.model";
import {
  EVENT_GALLERY_QUERY_KEY,
  EVENTS_QUERY_KEY,
  RSVPS_QUERY_KEY,
} from "@/src/query-keys/events.keys";
import gqlClient from "@/src/services/api/graphql.client";
import { sendNotification } from "@/src/services/api/notification/notification.service";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { router } from "expo-router";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import {
  DELETE_EVENT,
  DELETE_EVENT_GALLERY,
  DELETE_RSVP,
  GET_EVENT_GALLERY,
  GET_EVENTS,
  GET_RSVPS,
  INSERT_EVENT,
  INSERT_EVENT_GALLERY,
  INSERT_RSVP,
  UPDATE_EVENT,
  UPDATE_RSVP,
} from "./events.query";

const useInvalidateEvents = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] });
};

export const useCreateEventMutation = () => {
  const invalidate = useInvalidateEvents();
  const { user } = useAuth();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: InsertEventInput) => {
      const client = await gqlClient();
      const data = await client.request<InsertEventResponse>(INSERT_EVENT, {
        ...input,
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        email: user?.email ?? "",
        profileImageUrl: user?.profileImageUrl ?? "",
      });
      return data.insertevent;
    },
    onSuccess: (data) => {
      invalidate();

      // Broadcast to all users — use roles instead
      sendNotification({
        userIds: [""],
        denormalizedPayload: JSON.stringify({
          type: "event_created",
          message: t("NOTIFY_EVENT_CREATED", {
            firstName: user?.firstName,
            lastName: user?.lastName,
          }),
          actorAvatar: user?.profileImageUrl,
          actorName: `${user?.firstName} ${user?.lastName}`,
          actorId: user?.itemId,
          eventId: data.itemId,
          targetEntityId: data.itemId,
        }),
        configuratoinName: "event-created",
        responseKey: "event-action",
        responseValue: "event_created",
      }).catch((e) => console.error("Event notification failed:", e));

      router.push("/events");
      Toast.show({ type: "success", text1: "SUCCESS_EVENT_CREATED" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_EVENT_CREATION_FAILED" });
    },
  });
};

export const useGetCalendarEventsQuery = (input: GetEventsInput) => {
  return useQuery({
    queryKey: [
      EVENTS_QUERY_KEY,
      "calendar",
      input.filter,
      input.sort,
      input.pageNo,
      input.pageSize,
    ],
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetEventsResponse>(GET_EVENTS, {
        filter: input.filter,
        sort: input.sort,
        pageNo: input.pageNo,
        pageSize: input.pageSize,
      });
      return data.getevents;
    },
  });
};

export const useGetEventsQuery = (input: Omit<GetEventsInput, "pageNo">) => {
  return useInfiniteQuery({
    queryKey: [EVENTS_QUERY_KEY, input.filter, input.sort, input.pageSize],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      const data = await client.request<GetEventsResponse>(GET_EVENTS, {
        ...input,
        pageNo: pageParam,
      });
      return data.getevents;
    },
    getNextPageParam: (
      lastPage: GetEventsResponse["getevents"],
      pages: GetEventsResponse["getevents"][],
    ) => (lastPage.hasNextPage ? pages.length + 1 : undefined),
    initialPageParam: 1,
  });
};

export const useGetEventByIdQuery = (id: string) => {
  return useQuery({
    queryKey: ["getEventById", id],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_EVENTS, {
        filter: JSON.stringify({ ItemId: `${id}` }),
        sort: JSON.stringify({}),
      });
      return result.getevents.items[0] ?? null;
    },
    enabled: !!id && id !== "creation",
  });
};

export const useUpdateEventMutation = () => {
  const invalidate = useInvalidateEvents();
  return useMutation({
    mutationFn: async ({
      input,
      itemId,
    }: {
      input: InsertEventInput;
      itemId: string;
    }) => {
      const client = await gqlClient();
      const data = await client.request<InsertEventResponse>(UPDATE_EVENT, {
        filter: JSON.stringify({ ItemId: itemId }),
        ...input,
      });
      return data.insertevent;
    },
    onSuccess: () => {
      invalidate();
      router.push("/events");
      Toast.show({ type: "success", text1: "SUCCESS_EVENT_UPDATED" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_EVENT_UPDATE_FAILED" });
    },
  });
};

export const useDeleteEventMutation = () => {
  const invalidate = useInvalidateEvents();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const client = await gqlClient();
      const data = await client.request<DeleteEventResponse>(DELETE_EVENT, {
        filter: JSON.stringify({ ItemId: itemId }),
      });
      return data.deleteevent;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: "SUCCESS_EVENT_DELETED" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_EVENT_DELETE_FAILED" });
    },
  });
};

// RSVP

const useInvalidateRSVPs = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [RSVPS_QUERY_KEY] });
};

export const useGetUserRSVPQuery = (
  userId: string,
  eventId: string,
  options?: { enabled?: boolean },
) => {
  const filter = JSON.stringify({
    CreatedBy: userId,
    eventId,
  });

  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "user", userId, eventId],
    enabled: !!userId && !!eventId && (options?.enabled ?? true),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        filter,
        sort: "{}",
        pageNo: 1,
        pageSize: 1,
      });
      return data.getRSVPs.items[0] ?? null;
    },
  });
};

export const useGetAttendeeCountQuery = (
  eventId: string,
  options?: { enabled?: boolean },
) => {
  const filter = JSON.stringify({
    eventId,
    status: "Going",
  });

  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "counts", eventId],
    enabled: eventId.length > 0 && (options?.enabled ?? true),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        filter,
        sort: "{}",
        pageNo: 1,
        pageSize: 1,
      });
      return data.getRSVPs.totalCount;
    },
  });
};

export const useGetWaitlistedRSVPQuery = (
  eventId: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "waitlist", eventId],
    enabled: !!eventId && enabled,
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        filter: JSON.stringify({ eventId, status: "Waiting" }),
        sort: JSON.stringify({ CreatedDate: 1 }),
        pageNo: 1,
        pageSize: 1,
      });
      return data.getRSVPs.items[0] ?? null;
    },
  });
};

export const useGetRSVPsQuery = (eventId: string, status: RSVPStatus) => {
  const filter = JSON.stringify({ eventId, status });

  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "list", eventId, status],
    enabled: !!eventId,
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        filter,
        sort: JSON.stringify({ CreatedDate: 1 }),
        pageNo: 1,
        pageSize: 100,
      });
      return data.getRSVPs ?? null;
    },
  });
};

export const useInsertRSVPMutation = () => {
  const invalidate = useInvalidateRSVPs();
  return useMutation({
    mutationFn: async ({
      eventId,
      status,
      eventParticipantSnapshot,
    }: {
      eventId: string;
      status: RSVPStatus;
      eventParticipantSnapshot: RSVPParticipantSnapshot;
    }) => {
      const client = await gqlClient();
      const data = await client.request<InsertRSVPResponse>(INSERT_RSVP, {
        eventId,
        status,
        ...eventParticipantSnapshot,
      });
      return data.insertRSVP;
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

export const usePromoteFromWaitlistMutation = () => {
  const invalidate = useInvalidateRSVPs();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const client = await gqlClient();
      const data = await client.request<UpdateRSVPResponse>(UPDATE_RSVP, {
        filter: JSON.stringify({ ItemId: itemId }),
        status: "Going",
      });
      return data.updateRSVP;
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

export const useDeleteRSVPMutation = () => {
  const invalidate = useInvalidateRSVPs();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const client = await gqlClient();
      const data = await client.request<DeleteRSVPResponse>(DELETE_RSVP, {
        filter: JSON.stringify({ ItemId: itemId }),
      });
      return data.deleteRSVP;
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

export const useLeaveEventMutation = (
  eventId: string,
  capacity: number | null,
) => {
  const invalidate = useInvalidateRSVPs();
  const { mutateAsync: deleteRSVP } = useDeleteRSVPMutation();
  const { mutateAsync: promoteFromWaitlist } = usePromoteFromWaitlistMutation();
  const { data: attendeeCount = 0 } = useGetAttendeeCountQuery(eventId);
  const { data: firstWaitlisted } = useGetWaitlistedRSVPQuery(eventId, true);

  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      await deleteRSVP({ itemId });
      const wasFull = !!capacity && attendeeCount >= capacity;
      if (wasFull && firstWaitlisted) {
        await promoteFromWaitlist({ itemId: firstWaitlisted.ItemId });
      }
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

// Event Gallery
const useInvalidateEventGallery = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: [EVENT_GALLERY_QUERY_KEY] });
};

export const useGetEventGalleryQuery = (eventId: string) => {
  return useQuery({
    queryKey: [EVENT_GALLERY_QUERY_KEY, eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetEventGalleryResponse>(
        GET_EVENT_GALLERY,
        {
          filter: JSON.stringify({ eventId }),
          sort: JSON.stringify({ CreatedDate: -1 }),
          pageNo: 1,
          pageSize: 50,
        },
      );
      return data.geteventgallerys.items;
    },
  });
};

export const useInsertEventGalleryMutation = () => {
  const invalidate = useInvalidateEventGallery();
  return useMutation({
    mutationFn: async ({
      eventId,
      imageUrl,
    }: {
      eventId: string;
      imageUrl: string;
    }) => {
      const client = await gqlClient();
      const data = await client.request<InsertEventGalleryResponse>(
        INSERT_EVENT_GALLERY,
        { eventId, imageUrl },
      );
      return data.inserteventgallery;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: "SUCCESS_PHOTO_UPLOAD" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_PHOTO_UPLOAD" });
    },
  });
};

export const useDeleteEventGalleryMutation = () => {
  const invalidate = useInvalidateEventGallery();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const client = await gqlClient();
      const data = await client.request<DeleteEventGalleryResponse>(
        DELETE_EVENT_GALLERY,
        { filter: JSON.stringify({ ItemId: itemId }) },
      );
      return data.deleteeventgallery;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "error", text1: "SUCCESS_PHOTO_DELETE" });
    },
    onError: () => Toast.show({ type: "error", text1: "ERROR_PHOTO_DELETE" }),
  });
};
